<template>
    <div class="ui-page">
        <ui-page-header title="Painel RMA"
            subtitle="Fiscalização mensal: quem já enviou os documentos da competência, o que falta e quando foi cobrado.">
            <v-btn outlined :disabled="!rows.length" @click="exportRows"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar (.xlsx)</v-btn>
            <v-btn v-if="$allowed('rma.activate')" color="primary" depressed @click="openActivate"><v-icon left small>mdi-plus</v-icon>Ativar caso</v-btn>
        </ui-page-header>

        <div class="rma-period">
            <span class="ui-label mb-0">Competência</span>
            <v-select v-model="competence" :items="competenceItems" dense outlined hide-details class="rma-period__select" :menu-props="{ offsetY: true }"
                prepend-inner-icon="mdi-calendar-month-outline" aria-label="Competência"></v-select>
            <span class="ui-small ui-muted">Solicitação automática no dia 1 do mês seguinte; prazo e cobrança conforme cada caso.</span>
        </div>

        <section class="rma-stats mb-5" aria-label="Indicadores da competência">
            <component :is="kpi.filter ? 'button' : 'div'" v-for="kpi in kpis" :key="kpi.label" type="button" class="rma-stat"
                :class="{ 'rma-stat--clickable': kpi.filter, 'rma-stat--active': kpi.filter && kpi.filter !== 'all' && statusTab === kpi.filter }"
                :aria-pressed="kpi.filter ? String(statusTab === kpi.filter) : undefined" @click="kpi.filter && (statusTab = statusTab === kpi.filter ? 'all' : kpi.filter)">
                <span class="rma-stat__label">
                    <span v-if="kpi.tone" class="rma-stat__dot" :class="`rma-stat__dot--${kpi.tone}`"></span>{{ kpi.label }}
                </span>
                <span class="rma-stat__value" :class="kpi.tone && kpi.highlight ? `rma-stat__value--${kpi.tone}` : ''">{{ kpi.value }}</span>
                <v-progress-linear v-if="kpi.progress !== undefined" :value="kpi.progress" height="4" rounded color="primary" background-color="#eef0f3" class="rma-stat__bar"></v-progress-linear>
                <span class="rma-stat__hint">{{ kpi.hint }}</span>
            </component>
        </section>

        <div class="ui-list-bar">
            <ui-pills v-model="statusTab" :items="pillItems" />
            <ui-pills v-model="view" :items="viewItems" />
        </div>

        <template v-if="view === 'responsible'">
            <v-text-field v-model="search" placeholder="Buscar pelo nome do caso" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="rma-board-search mb-3"></v-text-field>
            <rma-responsible-board :rows="visibleRows" :me="data ? data.me : null" :loading="loading" @open="open" />
        </template>

        <ui-table-card v-else clearable :can-clear="!!search || statusTab !== 'all'" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="search" placeholder="Buscar pelo nome do caso" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
            </template>
            <v-data-table :headers="headers" :items="visibleRows" :loading="loading" disable-sort hide-default-footer item-key="client_id"
                :page.sync="page" :items-per-page="itemsPerPage" no-data-text="Nenhum caso com fiscalização mensal ativa" loading-text="Carregando..."
                class="ui-clickable-rows" @click:row="open">
                <template v-slot:[`item.client_name`]="{ item }">
                    <div class="ui-cell-main">{{ item.client_name }}</div>
                    <div class="ui-cell-sub">
                        <span v-if="!item.active">RMA desativado · </span>{{ item.companies }} {{ item.companies === 1 ? "empresa" : "empresas" }}
                        <span v-if="item.responsible"> · {{ item.responsible.name }}</span>
                    </div>
                    <div v-if="item.clarifications && (item.clarifications.open || item.clarifications.answered)" class="ui-cell-sub" style="color: #2b2050;">
                        <v-icon x-small>mdi-comment-question-outline</v-icon>
                        <span v-if="item.clarifications.answered">{{ item.clarifications.answered }} esclarecimento(s) respondido(s)</span><span v-if="item.clarifications.answered && item.clarifications.open"> · </span><span v-if="item.clarifications.open">{{ item.clarifications.open }} aguardando resposta</span>
                    </div>
                    <div v-if="item.pending_drafts" class="ui-cell-sub" style="color: #8a5a00;">
                        <v-icon x-small color="warning">mdi-email-edit-outline</v-icon> {{ item.pending_drafts === 1 ? "1 e-mail aguardando revisão" : `${item.pending_drafts} e-mails aguardando revisão` }}
                    </div>
                </template>
                <template v-slot:[`item.progress`]="{ item }">
                    <template v-if="item.opened">
                        <div class="d-flex align-center" style="gap: 8px; min-width: 150px;">
                            <v-progress-linear :value="percent(item)" height="6" rounded :color="item.status === 'complete' ? 'success' : 'primary'" background-color="#eef0f3"></v-progress-linear>
                            <span class="ui-nowrap ui-small">{{ item.counts.delivered }}/{{ item.counts.total }}</span>
                        </div>
                        <div class="ui-cell-sub">{{ item.counts.accepted }} conferidos<span v-if="item.counts.returned"> · {{ item.counts.returned }} devolvidos</span></div>
                    </template>
                    <span v-else class="ui-muted">—</span>
                </template>
                <template v-slot:[`item.counts.missing`]="{ item }">
                    <span v-if="!item.opened" class="ui-muted">—</span>
                    <strong v-else-if="item.counts.missing" :style="item.overdue ? 'color: #b91c1c;' : ''">{{ item.counts.missing }}</strong>
                    <span v-else class="ui-muted">0</span>
                </template>
                <template v-slot:[`item.due_date`]="{ item }">
                    <span class="ui-nowrap">{{ formatDate(item.due_date) }}</span>
                    <div v-if="item.overdue" class="ui-cell-sub" style="color: #b91c1c;">prazo encerrado</div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge set="rma-competence" :status="item.status" />
                    <div v-if="item.collection_stopped_at" class="ui-cell-sub">cobrança encerrada</div>
                </template>
                <template v-slot:[`item.last_charge_at`]="{ item }"><span class="ui-muted ui-nowrap">{{ item.last_charge_at ? formatDateTime(item.last_charge_at) : "—" }}</span></template>
                <template v-slot:[`item.actions`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>
            <template #footer>
                <ui-pagination :total="visibleRows.length" :page.sync="page" :items-per-page.sync="itemsPerPage" />
            </template>
        </ui-table-card>

        <v-dialog v-model="activate.open" max-width="520">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Ativar fiscalização mensal</h3>
                        <p class="ui-section-description">Escolha o caso. Na próxima tela você define os documentos, as empresas, o prazo e quem recebe as cobranças.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-autocomplete v-model="activate.client_id" :items="activate.clients" item-value="id" item-text="name" label="Caso"
                        outlined dense hide-details :loading="activate.loading" no-data-text="Nenhum caso encontrado"></v-autocomplete>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="activate.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :disabled="!activate.client_id" @click="goConfigure">Continuar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import RmaResponsibleBoard from "@/components/rma/RmaResponsibleBoard.vue";
import { competenceLabel, formatDate, formatDateTime, notifyError, previousCompetenceKey, recentCompetenceKeys } from "@/components/rma/rma.util";

const STATUS_TABS = [
    { value: "all", text: "Todos" },
    { value: "awaiting", text: "Aguardando envio", tone: "warning" },
    { value: "receiving", text: "Recebendo", tone: "info" },
    { value: "overdue", text: "Prazo encerrado", tone: "danger" },
    { value: "complete", text: "Completos", tone: "success" },
    { value: "not_opened", text: "Não abertas", tone: "neutral" },
];

const VIEW_KEY = "rma-dashboard-view";

function savedView() {
    try {
        return localStorage.getItem(VIEW_KEY) === "responsible" ? "responsible" : "list";
    } catch (error) {
        return "list";
    }
}

export default {
    name: "RmaDashboard",

    components: { RmaResponsibleBoard },

    data: () => ({
        view: savedView(),
        viewItems: [
            { value: "list", text: "Lista" },
            { value: "responsible", text: "Por responsável" },
        ],
        competence: previousCompetenceKey(),
        data: null,
        loading: false,
        search: "",
        statusTab: "all",
        page: 1,
        itemsPerPage: 25,
        headers: [
            { text: "Caso", value: "client_name" },
            { text: "Documentos enviados", value: "progress", width: 220 },
            { text: "Faltam", value: "counts.missing", width: 80, align: "center" },
            { text: "Prazo", value: "due_date", width: 130 },
            { text: "Situação", value: "status", width: 170 },
            { text: "Última cobrança", value: "last_charge_at", width: 150 },
            { text: "", value: "actions", align: "end", width: 32 },
        ],
        activate: { open: false, loading: false, clients: [], client_id: null },
    }),

    computed: {
        competenceItems() {
            return recentCompetenceKeys(24).map((key) => ({ value: key, text: competenceLabel(key) }));
        },
        rows() {
            return this.data ? this.data.rows : [];
        },
        searchedRows() {
            const term = this.search.trim().toLocaleLowerCase("pt-BR");
            return term ? this.rows.filter((row) => String(row.client_name).toLocaleLowerCase("pt-BR").includes(term)) : this.rows;
        },
        visibleRows() {
            if (this.statusTab === "all") return this.searchedRows;
            if (this.statusTab === "overdue") return this.searchedRows.filter((row) => row.overdue);
            return this.searchedRows.filter((row) => row.status === this.statusTab);
        },
        pillItems() {
            return STATUS_TABS.map((tab) => ({
                ...tab,
                count: tab.value === "all" ? this.searchedRows.length
                    : tab.value === "overdue" ? this.searchedRows.filter((row) => row.overdue).length
                        : this.searchedRows.filter((row) => row.status === tab.value).length,
            }));
        },
        kpis() {
            const t = this.data ? this.data.totals : {};
            const items = t.items || 0;
            const delivered = t.delivered || 0;
            return [
                { label: "Casos", value: t.cases || 0, hint: `${t.companies || 0} empresas fiscalizadas`, filter: "all" },
                { label: "Documentos enviados", value: `${delivered} de ${items}`, hint: items ? `${Math.round((100 * delivered) / items)}% do esperado` : "nenhuma competência aberta", progress: items ? (100 * delivered) / items : 0 },
                { label: "Sem nenhum envio", value: t.without_delivery || 0, hint: "aguardando o 1º envio", tone: "warning", highlight: !!t.without_delivery, filter: "awaiting" },
                { label: "Prazo encerrado", value: t.overdue || 0, hint: "com documento pendente", tone: "danger", highlight: !!t.overdue, filter: "overdue" },
                { label: "Completos", value: t.complete || 0, hint: "tudo recebido", tone: "success", highlight: !!t.complete, filter: "complete" },
                { label: "Não abertas", value: t.not_opened || 0, hint: "competência não aberta", tone: "neutral", filter: "not_opened" },
            ];
        },
    },

    watch: {
        competence() {
            this.load();
        },
        statusTab() {
            this.page = 1;
        },
        view(value) {
            try {
                localStorage.setItem(VIEW_KEY, value);
            } catch (error) {
                // preferencia so desta sessao
            }
        },
        search() {
            this.page = 1;
        },
    },

    created() {
        if (this.$route.query.competencia) this.competence = String(this.$route.query.competencia);
        this.load();
    },

    methods: {
        formatDate,
        formatDateTime,

        percent(row) {
            return row.counts.total ? Math.round((100 * row.counts.delivered) / row.counts.total) : 0;
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/rma/overview", { params: { competence: this.competence } });
                this.data = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        clearFilters() {
            this.search = "";
            this.statusTab = "all";
        },

        open(row) {
            const query = row.competence_id ? { competencia: row.competence_id } : {};
            this.$router.push({ path: `/fiscalizacao-mensal/casos/${row.client_id}`, query });
        },

        async openActivate() {
            this.activate = { open: true, loading: true, clients: this.activate.clients, client_id: null };
            try {
                if (!this.activate.clients.length) {
                    const result = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
                    const active = new Set(this.rows.filter((row) => row.active).map((row) => Number(row.client_id)));
                    this.activate.clients = result.data.content.data
                        .filter((client) => !active.has(Number(client.id)))
                        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
                }
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.activate.loading = false;
            }
        },

        goConfigure() {
            this.$router.push({ path: `/fiscalizacao-mensal/casos/${this.activate.client_id}`, query: { aba: "configuracao" } });
        },

        exportRows() {
            downloadXlsx(this.visibleRows.map((row) => ({
                Caso: row.client_name,
                Responsável: row.responsible ? row.responsible.name : "",
                Competência: this.data.label,
                "RMA ativo": row.active ? "Sim" : "Não",
                Empresas: row.companies,
                Situação: row.status_label,
                "Documentos esperados": row.counts.total,
                Enviados: row.counts.delivered,
                Conferidos: row.counts.accepted,
                Devolvidos: row.counts.returned,
                "Não se aplica": row.counts.not_applicable,
                Faltam: row.counts.missing,
                Prazo: formatDate(row.due_date),
                "Prazo encerrado": row.overdue ? "Sim" : "Não",
                "Solicitado em": row.requested_at ? formatDateTime(row.requested_at) : "",
                "Última cobrança": row.last_charge_at ? formatDateTime(row.last_charge_at) : "",
                "Cobrança encerrada": row.collection_stopped_at ? "Sim" : "Não",
            })), `Carteira_RMA_${this.competence}`);
        },
    },
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/ui/tokens";

.rma-period {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    margin-bottom: 12px;
}

.rma-board-search {
    max-width: 360px;
    background: $ui-bg;
}

.rma-period__select {
    max-width: 210px;
    background: $ui-bg;
}

// Indicadores em blocos iguais: rotulo, numero e uma linha de apoio.
.rma-stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    background: $ui-bg;
    border: 1px solid $ui-border;
    border-radius: $ui-radius;
    overflow: hidden;
}

.rma-stat {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    padding: 16px 18px;
    text-align: left;
    background: transparent;
    border: 0;
    font: inherit;
    color: inherit;

    & + & {
        border-left: 1px solid $ui-border-soft;
    }
}

.rma-stat--clickable {
    cursor: pointer;

    &:hover {
        background: $ui-bg-muted;
    }

    &:focus-visible {
        outline: 2px solid $ui-ring;
        outline-offset: -2px;
    }
}

.rma-stat--active {
    background: $ui-accent-soft;
    box-shadow: inset 0 -2px 0 $ui-accent;
}

.rma-stat__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    font-weight: 500;
    color: $ui-muted;
}

.rma-stat__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;

    @each $tone, $colors in $ui-tones {
        &--#{$tone} {
            background: nth($colors, 3);
        }
    }
}

.rma-stat__value {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.15;
    color: $ui-fg;
    white-space: nowrap;

    @each $tone, $colors in $ui-tones {
        &--#{$tone} {
            color: nth($colors, 2);
        }
    }
}

.rma-stat__bar {
    width: 100%;
    max-width: 160px;
    margin: 2px 0;
}

.rma-stat__hint {
    font-size: 12px;
    color: $ui-subtle;
}

@media (max-width: 1260px) {
    .rma-stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .rma-stat:nth-child(4) {
        border-left: 0;
    }

    .rma-stat:nth-child(n + 4) {
        border-top: 1px solid $ui-border-soft;
    }
}

@media (max-width: 640px) {
    .rma-stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .rma-stat:nth-child(odd) {
        border-left: 0;
    }

    .rma-stat:nth-child(4) {
        border-left: 1px solid $ui-border-soft;
    }

    .rma-stat:nth-child(n + 3) {
        border-top: 1px solid $ui-border-soft;
    }
}
</style>
